import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    CardActionArea,
    Grid,
    Chip,
    CircularProgress,
    Breadcrumbs,
    Link,
    alpha,
    useTheme,
    IconButton,
    Avatar,
    AvatarGroup
} from '@mui/material';
import {
    Add as AddIcon,
    NavigateNext as NavigateNextIcon,
    Groups as TeamIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Person as PersonIcon
} from '@mui/icons-material';
import AdminLayout from 'pages/admin/components/AdminLayout';
import { getAllTeams, TeamWithMembers } from 'services/teamService';

const TeamsStudioPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [teams, setTeams] = useState<TeamWithMembers[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedTeam, setSelectedTeam] = useState<TeamWithMembers | null>(null);

    useEffect(() => {
        fetchTeams();
    }, []);

    useEffect(() => {
        if (id && teams.length > 0) {
            const team = teams.find(t => t.id === parseInt(id));
            setSelectedTeam(team || null);
        } else {
            setSelectedTeam(null);
        }
    }, [id, teams]);

    const fetchTeams = async () => {
        setLoading(true);
        try {
            const response = await getAllTeams(1, 100, true);
            // Fetch detailed team info for each team
            const teamsWithDetails = await Promise.all(
                response.teams.map(async (team) => {
                    try {
                        const { getTeamById } = await import('services/teamService');
                        const detailedTeam = await getTeamById(team.id);
                        return detailedTeam;
                    } catch (error) {
                        console.error(`Error fetching team ${team.id}:`, error);
                        return null;
                    }
                })
            );
            setTeams(teamsWithDetails.filter((t): t is TeamWithMembers => t !== null));
        } catch (error) {
            console.error('Error fetching teams:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNew = () => {
        navigate('/admin/teams/new');
    };

    const getTeamColor = (index: number) => {
        const colors = [
            theme.palette.primary.main,
            theme.palette.secondary.main,
            theme.palette.success.main,
            theme.palette.warning.main,
            theme.palette.error.main,
            theme.palette.info.main
        ];
        return colors[index % colors.length];
    };

    // Grid view when no team selected
    if (!id) {
        return (
            <AdminLayout>
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box>
                            <Typography variant="h4" fontWeight="bold" gutterBottom>
                                Teams
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Organize students into collaborative learning groups
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleCreateNew}
                            disabled={loading}
                        >
                            New Team
                        </Button>
                    </Box>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Grid container spacing={3}>
                            {teams.map((team, index) => {
                                const teamColor = getTeamColor(index);
                                const memberCount = team.members?.length || 0;

                                return (
                                    <Grid item xs={12} sm={6} md={4} key={team.id}>
                                        <Card
                                            variant="outlined"
                                            sx={{
                                                height: '100%',
                                                transition: 'all 0.2s',
                                                background: `linear-gradient(135deg, ${alpha(teamColor, 0.08)} 0%, ${alpha(teamColor, 0.02)} 100%)`,
                                                borderColor: alpha(teamColor, 0.2),
                                                '&:hover': {
                                                    boxShadow: `0 8px 24px ${alpha(teamColor, 0.2)}`,
                                                    transform: 'translateY(-4px)',
                                                    borderColor: teamColor
                                                }
                                            }}
                                        >
                                            <CardActionArea onClick={() => navigate(`/admin/teams/${team.id}`)}>
                                                <CardContent>
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                                                        <Avatar
                                                            sx={{
                                                                width: 56,
                                                                height: 56,
                                                                bgcolor: alpha(teamColor, 0.15),
                                                                color: teamColor,
                                                                mr: 2
                                                            }}
                                                        >
                                                            <TeamIcon fontSize="large" />
                                                        </Avatar>
                                                        <Box sx={{ flexGrow: 1 }}>
                                                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                                                {team.name}
                                                            </Typography>
                                                            <Chip
                                                                icon={<PersonIcon fontSize="small" />}
                                                                label={`${memberCount} ${memberCount === 1 ? 'Member' : 'Members'}`}
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: alpha(teamColor, 0.1),
                                                                    color: teamColor,
                                                                    fontWeight: 'bold'
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>

                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                                                        {team.description || 'No description'}
                                                    </Typography>

                                                    {memberCount > 0 && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 28, height: 28, fontSize: '0.75rem' } }}>
                                                                {team.members?.slice(0, 4).map((member, idx) => (
                                                                    <Avatar
                                                                        key={idx}
                                                                        sx={{
                                                                            bgcolor: teamColor,
                                                                            fontSize: '0.7rem'
                                                                        }}
                                                                    >
                                                                        {member.user?.firstName?.[0]}{member.user?.lastName?.[0]}
                                                                    </Avatar>
                                                                ))}
                                                            </AvatarGroup>
                                                            {memberCount > 4 && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    +{memberCount - 4} more
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    )}
                                                </CardContent>
                                            </CardActionArea>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </Box>
            </AdminLayout>
        );
    }

    // Detail view when team selected
    return (
        <AdminLayout>
            <Box sx={{ mb: 3 }}>
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                    <Link
                        underline="hover"
                        color="inherit"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/admin/teams');
                        }}
                        sx={{ cursor: 'pointer' }}
                    >
                        All Teams
                    </Link>
                    {selectedTeam && (
                        <Typography color="text.primary" fontWeight="medium">
                            {selectedTeam.name}
                        </Typography>
                    )}
                </Breadcrumbs>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : selectedTeam ? (
                <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                    <Card variant="outlined" sx={{ p: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <Avatar
                                sx={{
                                    width: 80,
                                    height: 80,
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    color: theme.palette.primary.main,
                                    mr: 3
                                }}
                            >
                                <TeamIcon sx={{ fontSize: 40 }} />
                            </Avatar>
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="h4" fontWeight="bold" gutterBottom>
                                    {selectedTeam.name}
                                </Typography>
                                <Chip
                                    icon={<PersonIcon fontSize="small" />}
                                    label={`${selectedTeam.members?.length || 0} Members`}
                                    color="primary"
                                />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton color="primary">
                                    <EditIcon />
                                </IconButton>
                                <IconButton color="error">
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        <Typography variant="body1" color="text.secondary" paragraph>
                            {selectedTeam.description || 'No description provided'}
                        </Typography>

                        <Box sx={{ mt: 3 }}>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                Team Members
                            </Typography>
                            <Grid container spacing={2}>
                                {selectedTeam.members?.map((member, idx) => (
                                    <Grid item xs={12} sm={6} key={idx}>
                                        <Card variant="outlined" sx={{ p: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                                                    {member.user?.firstName?.[0]}{member.user?.lastName?.[0]}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body1" fontWeight="medium">
                                                        {member.user?.firstName} {member.user?.lastName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {member.user?.email}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>

                        <Grid container spacing={2} sx={{ mt: 3 }}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Created
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {new Date(selectedTeam.createdAt).toLocaleDateString()}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Last Updated
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {new Date(selectedTeam.updatedAt).toLocaleDateString()}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Card>
                </Box>
            ) : (
                <Box sx={{ textAlign: 'center', p: 4 }}>
                    <Typography color="error">Team not found</Typography>
                </Box>
            )}
        </AdminLayout>
    );
};

export default TeamsStudioPage;
